import React, { useEffect, useMemo, useState } from "react";
import { apiGet, apiPost } from "../../utils/api";
import { Card, CardBody, PageHeader, Spinner, Badge } from "../../components/ui";
import { FaSearch } from "react-icons/fa";

export default function AssignPlan() {
  const [users, setUsers] = useState([]);
  const [plans, setPlans] = useState([]);
  const [userQuery, setUserQuery] = useState("");
  const [selectedUser, setSelectedUser] = useState("");
  const [selectedPlan, setSelectedPlan] = useState("");
  const [interval, setInterval] = useState("month");
  const [periods, setPeriods] = useState(1);
  const [currentSub, setCurrentSub] = useState(null);
  const [currentPlan, setCurrentPlan] = useState(null);
  const [loadingSub, setLoadingSub] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState(null);

  useEffect(() => {
    apiGet("/api/admin/users")
      .then((res) => setUsers(res?.data || []))
      .catch((err) => console.error("fetchUsers error", err));
  }, []);

  useEffect(() => {
    apiGet("/api/plans")
      .then((res) => setPlans(res?.plans || []))
      .catch((err) => console.error("fetchPlans error", err));
  }, []);

  useEffect(() => {
    if (!selectedUser) {
      setCurrentSub(null);
      setCurrentPlan(null);
      return;
    }
    setLoadingSub(true);
    apiGet(`/api/admin/users/${selectedUser}/subscription`)
      .then((res) => {
        if (res?.success) {
          setCurrentSub(res.subscription || null);
          setCurrentPlan(res.plan || null);
        }
      })
      .catch((err) => console.error("fetchSubscription error", err))
      .finally(() => setLoadingSub(false));
  }, [selectedUser]);

  const filteredUsers = useMemo(() => {
    const q = userQuery.trim().toLowerCase();
    if (!q) return users.slice(0, 50);
    return users
      .filter((u) =>
        [u.email, u.name].some((v) => (v || "").toLowerCase().includes(q))
      )
      .slice(0, 50);
  }, [users, userQuery]);

  const selectedUserObj = useMemo(
    () => users.find((u) => u.id === selectedUser) || null,
    [users, selectedUser]
  );

  const handleAssign = async () => {
    if (!selectedUser || !selectedPlan) {
      setMessage({ type: "error", text: "Select a user and a plan first." });
      return;
    }
    setSubmitting(true);
    setMessage(null);
    try {
      const res = await apiPost(
        `/api/admin/users/${selectedUser}/assign-plan`,
        { planKey: selectedPlan, interval, periods: Number(periods) || 1 }
      );
      if (res?.success) {
        setMessage({ type: "success", text: "Plan assigned. The user will see the actual plan on their billing page." });
        // Refetch the subscription so the source badge updates immediately.
        const refreshed = await apiGet(`/api/admin/users/${selectedUser}/subscription`);
        if (refreshed?.success) {
          setCurrentSub(refreshed.subscription || null);
          setCurrentPlan(refreshed.plan || null);
        }
      } else {
        setMessage({ type: "error", text: res?.message || "Failed to assign plan." });
      }
    } catch (err) {
      console.error("assignPlan error", err);
      setMessage({ type: "error", text: err?.message || "Error assigning plan." });
    } finally {
      setSubmitting(false);
    }
  };

  const sourceBadge = (source) => {
    if (source === "stripe") return <Badge variant="success">Stripe Paid</Badge>;
    if (source === "trial") return <Badge variant="warning">Pro Trial</Badge>;
    if (source === "admin") return <Badge variant="info">Admin Assigned</Badge>;
    return <Badge>{source || "—"}</Badge>;
  };

  return (
    <div>
      <PageHeader
        title="Assign Plan"
        subtitle="Manually assign a subscription plan to a user. Admin-assigned plans show as the actual plan on the user's billing page (no 'Pro Trial' banner)."
      />

      <Card className="max-w-3xl">
        <CardBody className="space-y-5">
          {/* User search + select */}
          <div className="input-group">
            <label className="input-label">Find User</label>
            <div className="relative">
              <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-muted" size={12} />
              <input
                type="text"
                placeholder="Search by name or email…"
                value={userQuery}
                onChange={(e) => setUserQuery(e.target.value)}
                className="input pl-9"
              />
            </div>
          </div>

          <div className="input-group">
            <label className="input-label">Select User ({filteredUsers.length} shown)</label>
            <select
              value={selectedUser}
              onChange={(e) => setSelectedUser(e.target.value)}
              className="input"
              size={Math.min(8, Math.max(3, filteredUsers.length))}
            >
              <option value="">— Select user —</option>
              {filteredUsers.map((u) => (
                <option key={u.id} value={u.id}>
                  {u.email}
                  {u.name ? ` · ${u.name}` : ""}
                  {u.role && u.role !== "user" ? ` · ${u.role}` : ""}
                </option>
              ))}
            </select>
          </div>

          {/* Current subscription card */}
          {selectedUser && (
            <div className="p-4 rounded-lg border border-surface-border bg-surface-bg">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-semibold text-ink-primary">Current Subscription</h3>
                {loadingSub && <Spinner size="sm" />}
              </div>
              {!loadingSub && !currentSub && (
                <p className="text-sm text-ink-muted">No subscription on record for {selectedUserObj?.email}.</p>
              )}
              {currentSub && (
                <div className="grid grid-cols-1 md:grid-cols-4 gap-3 text-sm">
                  <div>
                    <span className="text-ink-muted block text-xs">Plan</span>
                    <span className="font-medium text-ink-primary">
                      {currentPlan?.name || currentSub.planKey}
                    </span>
                  </div>
                  <div>
                    <span className="text-ink-muted block text-xs">Source</span>
                    <span className="font-medium">{sourceBadge(currentSub.source)}</span>
                  </div>
                  <div>
                    <span className="text-ink-muted block text-xs">Status</span>
                    <span
                      className={`font-medium ${
                        currentSub.status === "active" ? "text-green-600" : "text-red-500"
                      }`}
                    >
                      {currentSub.status}
                    </span>
                  </div>
                  <div>
                    <span className="text-ink-muted block text-xs">Current period ends</span>
                    <span className="font-medium text-ink-primary">
                      {currentSub.currentPeriodEnd
                        ? new Date(currentSub.currentPeriodEnd).toLocaleDateString()
                        : "—"}
                    </span>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Plan + interval */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div className="input-group md:col-span-1">
              <label className="input-label">Plan</label>
              <select
                value={selectedPlan}
                onChange={(e) => setSelectedPlan(e.target.value)}
                className="input"
              >
                <option value="">— Select plan —</option>
                {plans.map((p) => (
                  <option key={p.id || p.key} value={p.key}>
                    {p.name} ({p.key})
                  </option>
                ))}
              </select>
            </div>
            <div className="input-group md:col-span-1">
              <label className="input-label">Interval</label>
              <select
                value={interval}
                onChange={(e) => setInterval(e.target.value)}
                className="input"
              >
                <option value="month">Monthly</option>
                <option value="year">Yearly</option>
                <option value="lifetime">Lifetime</option>
              </select>
            </div>
            <div className="input-group md:col-span-1">
              <label className="input-label">Periods</label>
              <input
                type="number"
                min={1}
                value={periods}
                onChange={(e) => setPeriods(e.target.value)}
                className="input"
                disabled={interval === "lifetime"}
              />
            </div>
          </div>

          <div className="flex items-center gap-3">
            <button
              onClick={handleAssign}
              disabled={submitting || !selectedUser || !selectedPlan}
              className="btn-primary"
            >
              {submitting ? (
                <>
                  <Spinner size="sm" /> Assigning…
                </>
              ) : (
                "Assign Plan"
              )}
            </button>
            {message && (
              <p
                className={`text-sm font-medium ${
                  message.type === "success" ? "text-green-600" : "text-red-500"
                }`}
              >
                {message.text}
              </p>
            )}
          </div>

          <p className="text-xs text-ink-muted">
            Note: assigning a plan here marks the subscription as <strong>Admin Assigned</strong>.
            The user will see the actual plan name on their billing page; the “Pro Trial” banner only
            appears for users on the signup trial who have not yet subscribed to a paid plan.
          </p>
        </CardBody>
      </Card>
    </div>
  );
}
